import { isEventVariableKey } from "@/pages/Private/pages/Messages/components/Variables/utils/variableRows";

import { ColumnConfig } from "../../../schema/columnConfig";
import { EventOutreachSchemaType } from "../../../schema/list";
import { getSuggestions, getSuggestionsMap } from "../../../utils/column-utils";

/**
 * The variable catalogue offered by the copywriting-instruction editor (PREC-3687 §4.1, AC 7).
 *
 * **It is the campaign editor's catalogue, not a second one.** `getSuggestions` /
 * `getSuggestionsMap` are the exact calls `CampaignPage` makes for a list with no lead selected
 * (`CampaignPage.tsx:635`), so a token that works in a campaign step works here, spelled the same
 * way, resolving to the same value.
 *
 * That is a deliberate correction of how this shipped in block 5. The first version invented a
 * `{{lead.firstName}}` catalogue that **nothing substituted anywhere in the product** — the tokens
 * reached the model as literal text and were resolved, if at all, by the model guessing from the
 * context blocks. The API now runs the campaign path's own substitution over the instruction
 * (`InstructionVariablesService`), so every token offered here is really replaced, and one with no
 * value becomes "" rather than leaking a placeholder into a message.
 */
export enum OutreachVariableGroup {
	/** The person we are writing to. */
	LEAD = "lead",
	/** Their company. */
	COMPANY = "company",
	/** The list's event-outreach fields, only while event outreach is on. */
	EVENT = "event",
	/** Values uploaded with the list (CSV columns, enrichment columns). */
	COLUMN = "column",
	/** The sending user, resolved per draft. */
	SENDER = "sender",
}

export interface OutreachVariable {
	/** The token as it is inserted into the instruction, e.g. `{prospect_firstName}`. */
	token: string;
	/** A live-looking value, so the user can see what the token stands for. */
	example: string;
}

export interface OutreachVariableGroupEntry {
	group: OutreachVariableGroup;
	entries: OutreachVariable[];
}

/** Display order of the groups. */
const GROUP_ORDER: OutreachVariableGroup[] = [
	OutreachVariableGroup.LEAD,
	OutreachVariableGroup.COMPANY,
	OutreachVariableGroup.EVENT,
	OutreachVariableGroup.COLUMN,
	OutreachVariableGroup.SENDER,
];

/** Keys that resolve from the sending user rather than the lead (cf. `variableRows.SENDER_KEYS`). */
const SENDER_KEYS = new Set(["senderFirstName", "senderLastName", "signature", "unsubscribe_link"]);

/**
 * Which group a flattened suggestion key belongs to.
 *
 * Prefix-based because the keys are `flatten`ed paths: everything under the prospect's organization
 * is company data, everything else under the prospect is lead data.
 */
function groupOf(
	key: string,
	listEvent: EventOutreachSchemaType | null | undefined
): OutreachVariableGroup {
	if (isEventVariableKey(key, listEvent)) {
		return OutreachVariableGroup.EVENT;
	}

	if (SENDER_KEYS.has(key)) {
		return OutreachVariableGroup.SENDER;
	}

	if (key.startsWith("column_")) {
		return OutreachVariableGroup.COLUMN;
	}

	if (key.startsWith("prospect_organization_")) {
		return OutreachVariableGroup.COMPANY;
	}

	return OutreachVariableGroup.LEAD;
}

/**
 * The groups the Variables dropdown shows.
 *
 * Both arguments are the list's own — the uploaded column configs and the event row — because those
 * are what make `{column_<id>_<name>}` and the `{event_*}` tokens exist at all. A group with no
 * entries is omitted rather than rendered empty.
 */
export function getVariableGroups(
	listColumns?: ColumnConfig[],
	listEvent?: EventOutreachSchemaType | null
): OutreachVariableGroupEntry[] {
	const examples = getSuggestionsMap(undefined, true, listColumns, listEvent);
	const keys = getSuggestions(undefined, true, listColumns, listEvent);

	const byGroup = new Map<OutreachVariableGroup, OutreachVariable[]>();

	for (const key of keys) {
		// The `{key|label}` duplicates the editors emit are not offered separately — the label half
		// is display sugar the substituter strips anyway.
		if (key.includes("|")) {
			continue;
		}

		const value = examples[key];
		const group = groupOf(key, listEvent);

		byGroup.set(group, [
			...(byGroup.get(group) ?? []),
			{ token: `{${key}}`, example: value == null ? "" : String(value) },
		]);
	}

	return GROUP_ORDER.filter((group) => byGroup.get(group)?.length).map((group) => ({
		group,
		entries: byGroup.get(group) as OutreachVariable[],
	}));
}

/** Case-insensitive match over both the token and its example, for the dropdown's search box. */
export function filterVariableGroups(
	groups: OutreachVariableGroupEntry[],
	query: string
): OutreachVariableGroupEntry[] {
	const needle = query.trim().toLowerCase();

	if (!needle) {
		return groups;
	}

	return groups
		.map((group) => ({
			group: group.group,
			entries: group.entries.filter((entry) =>
				`${entry.token}${entry.example}`.toLowerCase().includes(needle)
			),
		}))
		.filter((group) => group.entries.length > 0);
}
